import { useEffect, useState } from 'react';
import { 
  fetchTopGatewaysByRewards, 
  TopGatewayRewards,
  RewardsTimePeriod
} from '../services/RewardsService';
import CrownIcon from '../components/icons/CrownIcon';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useGateways } from '../context/GatewayContext';
import { useWalletContext } from '../context/WalletContext';
import PageContainer from '../components/layout/PageContainer';

interface LeaderboardState {
  isLoading: boolean;
  error: string | null;
}

const ITEMS_PER_PAGE = 10;

const LeaderboardPage: React.FC = (): JSX.Element => {
  const { gateways } = useGateways();
  const { isConnected } = useWalletContext();
  const [timePeriod, setTimePeriod] = useState<RewardsTimePeriod>(RewardsTimePeriod.CURRENT_MONTH);
  const [currentMonthGateways, setCurrentMonthGateways] = useState<TopGatewayRewards[] | null>(null);
  const [lastWeekGateways, setLastWeekGateways] = useState<TopGatewayRewards[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [leaderboardState, setLeaderboardState] = useState<LeaderboardState>({
    isLoading: false,
    error: null
  });

  // Get user's gateway IDs
  const userGatewayIds = new Set(gateways.map(g => g.id));

  // Filter data based on search query
  const filteredData = (currentData: TopGatewayRewards[] | null) => {
    if (!currentData) return null;
    if (!searchQuery) return currentData;
    
    return currentData.filter(gateway => 
      gateway.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gateway.gwId.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Get current data with filters applied
  const currentData = filteredData(timePeriod === RewardsTimePeriod.CURRENT_MONTH ? currentMonthGateways : lastWeekGateways);
  const totalPages = currentData ? Math.ceil(currentData.length / ITEMS_PER_PAGE) : 0;

  // Get current page data
  const getCurrentPageData = () => {
    if (!currentData) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return currentData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  // Get user's gateways that are not in the current page
  const getUserGatewaysNotInCurrentPage = () => {
    // Only show on the first page, not on other pages
    if (!currentData || !isConnected || currentPage !== 1) return [];
    const currentPageData = getCurrentPageData();
    const currentPageIds = new Set(currentPageData.map(g => g.gwId));
    
    return currentData.filter(gateway => 
      userGatewayIds.has(gateway.gwId) && !currentPageIds.has(gateway.gwId)
    );
  };

  // Reset to first page when time period or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [timePeriod, searchQuery]);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      setLeaderboardState({
        isLoading: true,
        error: null
      });
      
      try {
        // Fetch top gateways for current month
        const currentMonthGatewaysData = await fetchTopGatewaysByRewards(RewardsTimePeriod.CURRENT_MONTH);
        setCurrentMonthGateways(currentMonthGatewaysData);
        
        // Fetch top gateways for last week
        const lastWeekGatewaysData = await fetchTopGatewaysByRewards(RewardsTimePeriod.LAST_FULL_WEEK);
        setLastWeekGateways(lastWeekGatewaysData);
        
        setLeaderboardState({
          isLoading: false,
          error: null
        });
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setLeaderboardState({
          isLoading: false,
          error: 'Failed to load leaderboard data'
        });
      }
    };
    
    fetchLeaderboardData();
  }, []);

  // Helper functions
  const formatGatewayName = (name: string, gwId: string) => {
    return name || `Gateway ${gwId.slice(0, 6)}...`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Pagination controls
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Get visible page numbers
  const getVisiblePages = () => {
    const maxVisible = 5; // Always show 5 page numbers
    const pages = [];

    if (totalPages <= maxVisible) {
      // If we have 5 or fewer pages, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate the range of pages to show
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = start + maxVisible - 1;

      // Adjust if we're near the end
      if (end > totalPages) {
        end = totalPages;
        start = Math.max(1, end - maxVisible + 1);
      }

      // Add the pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // Calculate tier based on percentile
  const calculateTier = (index: number, total: number) => {
    const percentile = (index + 1) / total * 100;
    if (percentile <= 20) return "★★★"; // Top 20%
    if (percentile <= 50) return "★★"; // Next 30%
    return "★"; // Remaining 50%
  };

  // Function to render a gateway row
  const renderGatewayRow = (gateway: TopGatewayRewards, rank: number, tier: string, isUserGateway: boolean = false) => (
    <tr 
      key={gateway.gwId} 
      className={`border-b border-gray-200 dark:border-[#292929] hover:bg-gray-100 dark:hover:bg-[#292929] transition-all ${
        isUserGateway ? 'relative bg-gray-100 dark:bg-[#292929]/30 shadow-[0_0_10px_0_rgba(0,255,132,0.2)]' : ''
      }`}
    >
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">#{rank}</td>
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
        <a 
          href={`https://explorer.sourceful.energy/${gateway.gwId.substring(0, 16)}/${gateway.gwId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`hover:text-gray-900 dark:hover:text-[#00FF84] transition-colors ${
            isUserGateway ? 'text-[#00FF84]' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {formatGatewayName(gateway.name, gateway.gwId)}
        </a>
      </td>
      <td className="px-4 py-3 text-right text-gray-700 dark:text-[#00FF84] font-medium">{formatNumber(gateway.amount)}</td>
      <td className="px-4 py-3 text-right text-gray-700 dark:text-[#00FF84] font-medium">{tier}</td>
    </tr>
  );

  return (
    <PageContainer>
      <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <CrownIcon className="w-5 h-5 text-green-600 dark:text-[#00FF84]" />
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Leaderboard</h1>
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-400">See the top performing gateways in the Sourceful network</p>
      </div>

      {/* Leaderboard */}
      <div className="mb-12">
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Gateway Leaderboard</h2>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${currentData?.length || 0} gateways`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 rounded-[8px] border border-gray-200 dark:border-[#393939] focus:outline-none focus:border-[#00FF84] focus:ring-1 focus:ring-[#00FF84]"
                />
              </div>
              <div className="rounded-[8px] flex">
                <button
                  className={`px-4 py-2 rounded-l-[20px] transition-colors ${
                    timePeriod === RewardsTimePeriod.CURRENT_MONTH
                      ? 'bg-[#00FF84] text-black font-medium'
                      : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'
                  }`}
                  onClick={() => setTimePeriod(RewardsTimePeriod.CURRENT_MONTH)}
                >
                  Current Month
                </button>
                <button
                  className={`px-4 py-2 rounded-r-[20px] transition-colors ${
                    timePeriod === RewardsTimePeriod.LAST_FULL_WEEK
                      ? 'bg-[#00FF84] text-black font-medium'
                      : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'
                  }`}
                  onClick={() => setTimePeriod(RewardsTimePeriod.LAST_FULL_WEEK)}
                >
                  Last Week
                </button>
              </div>
            </div>
          </div>

          {/* Top Gateways Table */}
          <div className="space-y-4">
            {leaderboardState.error ? (
              <p className="text-gray-500 dark:text-gray-400">{leaderboardState.error}</p>
            ) : leaderboardState.isLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="bg-gray-200 dark:bg-[#292929] h-12 w-full rounded"></div>
                <div className="bg-gray-200 dark:bg-[#292929] h-12 w-full rounded"></div>
                <div className="bg-gray-200 dark:bg-[#292929] h-12 w-full rounded"></div>
              </div>
            ) : currentData?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-[#292929]">
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Rank</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Gateway</th>
                      <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Points</th>
                      <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentPageData().map((gateway) => {
                      // Find the original index in the unfiltered data to maintain correct ranking
                      const originalData = timePeriod === RewardsTimePeriod.CURRENT_MONTH ? currentMonthGateways : lastWeekGateways;
                      const originalIndex = originalData?.findIndex(g => g.gwId === gateway.gwId) ?? -1;
                      const rank = originalIndex + 1;
                      const tier = calculateTier(originalIndex, originalData?.length || 0);
                      const isUserGateway = userGatewayIds.has(gateway.gwId);
                      
                      return renderGatewayRow(gateway, rank, tier, isUserGateway);
                    })}

                    {/* Show user's gateways that are not in the current page */}
                    {getUserGatewaysNotInCurrentPage().map((gateway) => {
                      const originalData = timePeriod === RewardsTimePeriod.CURRENT_MONTH ? currentMonthGateways : lastWeekGateways;
                      const originalIndex = originalData?.findIndex(g => g.gwId === gateway.gwId) ?? -1;
                      const rank = originalIndex + 1;
                      const tier = calculateTier(originalIndex, originalData?.length || 0);
                      
                      return renderGatewayRow(gateway, rank, tier, true);
                    })}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 dark:bg-[#292929] text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'
                      }`}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    <div className="flex items-center gap-2">
                      {getVisiblePages().map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-8 h-8 flex items-center justify-center rounded-md ${
                            currentPage === page
                              ? 'bg-[#00FF84] text-black font-medium'
                              : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 dark:bg-[#292929] text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'
                      }`}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No gateways found matching your search</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-12 text-center text-gray-500 text-sm">
        <p>Rewards data is updated daily. The leaderboard shows the top performers for the selected time period.</p>
      </div>
    </PageContainer>
  );
};

export default LeaderboardPage;
